import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
    Alert,
    Box,
    CircularProgress,
    Container,
    Divider,
    FormControl,
    FormControlLabel,
    Grid,
    Paper,
    Radio,
    RadioGroup,
    Typography,
    useTheme,
} from '@mui/material';
import SelectField from '../../../components/ui/SelectField';
import InputField from '../../../components/ui/InputField';
import { useIsInvoiceIssuingTenant } from '../../../hooks/useIsInvoiceIssuingTenant';
import PriceAdjustmentReasonField from '../../../components/PriceAdjustmentReasonField';
import DateRangeField from '../../../components/ui/DateRangeField';
import { useSelector } from 'react-redux';
import { RootState } from '../../../store/store';
import { PricePreviewArgs } from '../../../api/booking/pricePreviewApiTypes';
import { usePricePreview } from '../../../hooks/usePricePreview';
import BookingCustomerClassSelect from '../CreateBooking/components/BookingCustomerClassSelect';
import { useEditBookingCustomerClass } from './hooks/useEditBookingCustomerClass';
import { useDispatch } from 'react-redux';
import {
    clearBookingCreationStates,
    clearEditCheckOutDate,
    setCity,
    setEditAddress,
    setEditBookingDetails,
    setEditCheckInDate,
    setEditCheckOutDate,
    setEditComment,
    setEditCompany,
    setEditCountry,
    setEditEmail,
    setEditEmailNote,
    setEditFirstName,
    setEditInvoiceNumber,
    setEditLastName,
    setEditNumberOfGuests,
    setEditPersoCity,
    setEditPhone,
    setEditZipCode,
    setForSomeoneElse,
    setRevertEditBookingReservation,
    setSelectedEditApartment,
    setSelectedEditApartmentDetails,
    setSomeoneElseEditEmail,
    setSomeoneElseEditFirstName,
    setSomeoneElseEditLastName,
    setSomeoneElseEditPhone,
} from '../../../store/slices/bookingCreationSlice';
import dayjs, { Dayjs } from 'dayjs';
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore';
import PhoneNumberComponent from '../../common/PhoneNumberComponent';
import { useCountryOptions } from '../../../hooks/useCountryOptions';
import { Trans, useTranslation } from 'react-i18next';
import {
    EditRoomRentalBookingPayload,
    PersonalDataValidation,
} from '../../../types/bookingCreation';
import { useNavigate, useParams } from 'react-router';
import StatusBadge from '../../../components/StatusBadge';
import Logger from '../../../common/logger';
import CustomButton from '../../../components/CustomButton';
import CustomerMailBlockedNote from '../../../components/booking/CustomerMailBlockedNote';
import { customerMailRefusalOf, savedOutcomeToast } from '../../../utils/customerMailGuard';
import BookingAttributionFields, {
    BookingAttribution,
    EMPTY_BOOKING_ATTRIBUTION,
} from '../../../components/booking/BookingAttributionFields';
import { applyContactPrefill } from '../../../utils/organizationPrefill';
import { attributionPayloadForEdit } from '../../../utils/costCenter';
import ReservationSummary from '../../../components/ReservationSummary';
import { useGetBookingDetailsQuery } from '../../../api/booking/bookingApiSlice';
import { pickedOrStoredInstant, stationDateTime, zoneOfLocation } from '../../../utils/stationTime';
import { useLocationTimeZone } from '../../../hooks/useLocationTimeZone';
import { BOOKING_EMAIL_NOTE_MAX_LENGTH, extractAddress, formatDateForDisplay, formatDateTime, sortByLocaleName } from '../../../utils';
import CustomSeparator from '../../../components/CustomBreadCrumb';
import { makeRentalBookingDetailsUrl, paths } from '../../../routes';
import { useGetLocationListQuery } from '../../../api/locations/locationsApiSlice';
import { useFetchApartments } from '../../../flows/fetchApartmentsFlow';
import { useGuestCapacityGuard } from '../../../hooks/useGuestCapacityGuard';
import BookingEditDetailsAdditionalServices from './components/AdditionalServices/BookingEditDetailsAdditionalServices';
import { useEditServiceRemapOnAssetSwitch } from './hooks/useEditServiceRemapOnAssetSwitch';
import { AdditionalService as AdditionalServiceType } from '../../../types/bookingCreation';
import { useFetchApartmentDetails } from '../../../flows/fetchApartmentDetailsFlow';
import { editFailureMessageKey, useEditRoomRentalBooking } from '../../../flows/editRoomRentalBookingFlow';
import SnackbarComponent from '../../common/SnackbarComponent';
import i18n from 'i18next';
import InfoTooltip from '../../../components/InfoTooltip';
import CustomModal from '../../common/CustomModal';
import BookingDetailsAdditionalServices from '../CreateBooking/components/AdditionalServices/BookingDetailsAdditionalServices';
import PageHeader from '../../../components/PageHeader';
import PageHeadline from '../../../components/PageHeadline';
import { usePriceOverrideGuard } from '../../../hooks/usePriceOverrideGuard';
import { coveredByOf, editPaymentBadgeStatus } from '../../../utils/bookingCoverage';
import { commentLabelKey } from '../../../utils/bookingCommentLabel';
import { saveAndSendCopyKeys, toApprovalState } from '../../../utils/bookingApproval';

dayjs.extend(isSameOrBefore);

const EditRoomBooking = () => {
    const dispatch = useDispatch();
    const { t } = useTranslation();
    const {
        options: countryOptions,
        toLabel: toCountryLabel,
        toValue: toCountryValue,
    } = useCountryOptions();
    const navigate = useNavigate();
    const theme = useTheme();
    const [cities, setCities] = useState<string[]>([]);
    const [apartmentNames, setApartmentNames] = useState<string[]>([]);
    const [loadingCities, setLoadingCities] = useState(true);
    const [performRevert, setPerformRevert] = useState(true);
    const [open, setOpen] = useState(false);
    const [alreadyPaid, setAlreadyPaid] = useState<number>(0);
    const [discount, setDiscount] = useState<number>(0);
    // #1000 Step 7C: reason for a manual price adjustment (sent with the
    // override when the admin changes the price).
    const [priceAdjustmentReason, setPriceAdjustmentReason] = useState<string>('');
    const [, setLoadingApartments] = useState(false);
    const [originalApartment, setOriginalApartment] = useState<string>();
    const [bookingFor, setBookingFor] = useState<string>(
        'customer',
    );
    const [editDataLoaded, setEditDataLoaded] = useState<boolean>(true);
    const [loadingEditBookingOnlySave, setLoadingEditBookingOnlySave] = useState(false);
    const [loadingEditBookingSaveAndSend, setLoadingEditBookingSaveAndSend] = useState(false);
    const [snackbarData, setSnackbarData] = useState({
        open: false,
        message: '',
        severity: 'info' as 'success' | 'info' | 'warning' | 'error',
    });

    const params = useParams<{ bookingId: string }>();
    const orderId = params.bookingId ?? '';

    const { data, isFetching } = useGetBookingDetailsQuery({
        orderId,
    });
    // M8 approval (#1809): on a reservation still awaiting approval, "save and
    // send email" IS the approval (the server records it once the mail is
    // attempted), so the button and its confirm say so. "Save only" leaves it
    // awaiting.
    const sendCopy = saveAndSendCopyKeys(toApprovalState(data?.approval?.state) === 'awaiting');

    // #1810: organisation (unit) and the invoice recipient's cost centre.
    // Seeded ONCE per booking from the API — afterwards the form owns it, so a
    // background refetch cannot overwrite what the operator is editing.
    const [attribution, setAttribution] = useState<BookingAttribution>(EMPTY_BOOKING_ATTRIBUTION);
    const attributionSeededFor = useRef<string | null>(null);
    useEffect(() => {
        // Only a stored attribution that was actually READ seeds the form: on
        // a failed read the fields stay empty and nothing is sent, and a later
        // successful refetch seeds them then — an empty seed from the failed
        // read must never be what a save sends back.
        if (!data || data.id !== orderId || data.attributionStatus !== 'ok') return;
        if (attributionSeededFor.current === orderId) return;
        attributionSeededFor.current = orderId;
        setAttribution({
            organizationId: data.organizationId ?? null,
            organizationUnitId: data.organizationUnitId ?? null,
            costCenter: data.costCenter ?? '',
        });
    }, [data, orderId]);

    // #1808: the booking's class, changeable here as on create (staging
    // finding 6). A change reprices the stay under the new class on save, and
    // the price preview quotes that already.
    const {
        customerClassId,
        setCustomerClassId,
        seeded: classSeeded,
        classFields,
        // "No override" is named after the class clearing would apply.
        noOverrideClassId,
    } = useEditBookingCustomerClass(data, orderId);

    const editRoomRentalBooking = useEditRoomRentalBooking(orderId);
    const fetchApartmentDetails = useFetchApartmentDetails(true);

    const breadcrumbItems = [
        { label: t('booking_details.booking'), href: paths.rentalBooking },
        { label: data?.bookingNumber ?? '', href: makeRentalBookingDetailsUrl(orderId) },
        { label: t('booking_details.edit'), isActive: true },
    ];

    const onFirstNameChange = (firstName: string) =>
        dispatch(setEditFirstName({ firstName }));
    const onLastNameChange = (lastName: string) =>
        dispatch(setEditLastName({ lastName }));
    const onPhoneChange = (phone: string) => {
        dispatch(setEditPhone({ phone: phone }));
    };
    const onCompanyNameChange = (company: string) => {
        dispatch(setEditCompany({ company: company }));
    };
    const onCommentChange = (comment: string) => {
        dispatch(setEditComment({ comment: comment }));
    };
    const onEmailNoteChange = (emailNote: string) => {
        dispatch(setEditEmailNote({ emailNote: emailNote }));
    };
    const onEmailChange = (email: string) => dispatch(setEditEmail({ email }));

    const onAddressChange = (address: string) =>
        dispatch(setEditAddress({ address }));
    const onCityChange = (city: string) => dispatch(setEditPersoCity({ city }));
    const onCountryChange = (country: string) => {
        dispatch(setEditCountry({ country: country }));
    };
    const onZipCodeChange = (zipCode: string) => {
        dispatch(setEditZipCode({ zipCode }));
    };

    const onSomeoneElseFirstNameChange = (firstName: string) => {
        dispatch(setSomeoneElseEditFirstName({ firstName: firstName }));
    };
    const onSomeoneElseLastNameChange = (lastName: string) => {
        dispatch(setSomeoneElseEditLastName({ lastName: lastName }));
    };
    const onSomeoneElseEmailChange = (email: string) => {
        dispatch(setSomeoneElseEditEmail({ email: email }));
    };
    const onSomeoneElsePhoneChange = (phone: string) => {
        dispatch(setSomeoneElseEditPhone({ phone: phone }));
    };

    const selectedApartment = useSelector(
        (state: RootState) => state.bookingCreation.selectedEditApartment,
    );
    // #3219: the stored booking is read on its own location's clock; the
    // picked days are built on the clock of the apartment being booked (the
    // same location unless the operator moves the stay to another house).
    const bookingTimeZone = zoneOfLocation(data?.locationTimeZone);
    const timeZone = useLocationTimeZone(selectedApartment?.locationId, data?.locationTimeZone);

    const personalDetails = useSelector(
        (state: RootState) =>
            state.bookingCreation.personalData,
    );

    const someoneElseDetails = useSelector(
        (state: RootState) =>
            state.bookingCreation.someoneElseData,
    );

    const city = useSelector(
        (state: RootState) => state.bookingCreation.editBookingDetails?.city,
    );
    const startDate = useSelector(
        (state: RootState) => state.bookingCreation.editBookingDetails?.checkIn,
    );
    const endDate = useSelector(
        (state: RootState) => state.bookingCreation.editBookingDetails?.checkOut,
    );
    const numberOfGuests = useSelector(
        (state: RootState) =>
            state.bookingCreation.editBookingDetails?.numberOfGuests,
    );
    const isInvoiceIssuingTenant = useIsInvoiceIssuingTenant();
    const invoiceNumber = useSelector(
        (state: RootState) => state.bookingCreation.editBookingDetails?.invoiceNumber,
    );
    const comment = useSelector(
        (state: RootState) =>
            state.bookingCreation.editBookingDetails?.comment,
    );
    const createdBy = useSelector(
        (state: RootState) =>
            state.bookingCreation.editBookingDetails?.createdBy,
    );
    const emailNote = useSelector(
        (state: RootState) =>
            state.bookingCreation.editBookingDetails?.emailNote,
    );

    const apartments = useSelector(
        (state: RootState) => state.bookingCreation.apartments,
    );

    const personalDataValidation: PersonalDataValidation = useSelector(
        (state: RootState) => state.bookingCreation.personalDataValidation,
    );

    const editBookingDetails = useSelector(
        (state: RootState) => state.bookingCreation.editBookingDetails,
    );
    const additionalServices = useSelector(
        (state: RootState) =>
            state.bookingCreation.editBookingDetails.additionalService ?? [],
    );

    const selectedApartmentDetails = useSelector(
        (state: RootState) => state.bookingCreation.selectedEditApartmentDetails,
    );

    const confirmEnabled = useSelector(
        (state: RootState) => state.bookingCreation.confirmEnabled,
    );

    const allAdditionalServices: AdditionalServiceType[] | null =
            selectedApartmentDetails?.assetAdditionalServices ?? null;

    // #1734: only restrict the service menu to what existed at booking time
    // while the booking is still on its original asset. Switching asset during
    // edit re-prices against that asset's current offering, so its newer
    // services must stay selectable.
    const bookedAssetStillSelected =
        !!data?.apartment?.id && selectedApartment?.assetId === data.apartment.id;

    const { servicesUnavailableAtVenue, markPendingRemap } = useEditServiceRemapOnAssetSwitch();

    const styles = {
        paper: (theme: any) => ({
            padding: '20px',
        }),
    };

    const generateAvailableDates = (minDate?: Dayjs | null) => {
        const start = minDate || dayjs().startOf('day');
        const end = dayjs().add(18, 'month').endOf('month').startOf('day');
        const dates: string[] = [];

        for (let date = start; date.isBefore(end); date = date.add(1, 'day')) {
            dates.push(date.format('YYYY-MM-DD'));
        }

        return dates;
    };

    const generateAvailableCheckOutDates = (checkInDate: Dayjs | null) => {
        if (!checkInDate) return generateAvailableDates();

        const start = checkInDate?.add(1, 'day');
        const end = dayjs()?.add(18, 'month').endOf('month').startOf('day');
        const dates: string[] = [];

        for (let date = start; date.isBefore(end); date = date?.add(1, 'day')) {
            dates.push(date.format('YYYY-MM-DD'));
        }

        return dates;
    };


    const availableCheckInDates = generateAvailableDates();
    const availableCheckOutDates = generateAvailableCheckOutDates(startDate);

    const handleDateChange = (start: Dayjs | null, end: Dayjs | null) => {
        if (start) {
            // Clear the *edit* checkout, not the create-flow one — otherwise a
            // restarted selection (outside click) leaves the stale checkout in
            // place and the form shows an inverted range like "04 Aug – 03 Aug".
            // Cleared first so setEditCheckInDate validates against it.
            dispatch(clearEditCheckOutDate());
            dispatch(setEditCheckInDate({ checkInDate: start.startOf('day') }));
        }
        if (end) {
            if (start && end.isSame(start, 'day')) {
                // Ensure checkout date is at least one day after check-in date
                dispatch(
                    setEditCheckOutDate({
                        checkOutDate: start.clone().add(1, 'day'),
                    }),
                );
            } else {
                dispatch(setEditCheckOutDate({ checkOutDate: end }));
            }
        }
    };

    const currentLanguage = i18n.language;

    useEffect(() => {
        if (apartments) {
            setApartmentNames(sortByLocaleName(apartments.map((apart) => {
                if (apart?.langNames?.[currentLanguage]?.title) {
                    return apart?.langNames?.[currentLanguage]?.title;
                } else {
                    return apart?.name;
                }
            }), (name) => name));
        }
    }, [apartments, currentLanguage]);

    const { data: locations } = useGetLocationListQuery('apartment');

    useEffect(() => {
        if (locations && locations.length > 0) {
            setCities(
                Array.from(
                    new Set(locations.map((loc: any) => loc.address.city)),
                ),
            );
            setLoadingCities(false);
        }
    }, [locations]);

    const fetchApartments = useFetchApartments(
        stationDateTime(startDate, undefined, timeZone),
        stationDateTime(endDate, undefined, timeZone),
        city,
        numberOfGuests,
        orderId,
        data?.useCase, // useCase from booking data
    );
    useEffect(() => {
        const getApartments = async () => {
            if (city && startDate && endDate && numberOfGuests && fetchApartments) {
                setLoadingApartments(true);
                await fetchApartments({
                    includeCurrentApartment: true,
                });
            }
        };
        getApartments();
    }, [city, startDate, endDate, numberOfGuests]);

    useEffect(() => {
        if (data && performRevert && apartments && apartments.length > 0) {
            const selected = apartments?.find(
                (apartment) =>
                    apartment.name === data.apartment.name,
            );
            if (selected) {
                dispatch(
                    setSelectedEditApartment(selected),
                );
            }
            setPerformRevert(false);
        }
    }, [apartments, performRevert]);

    useEffect(() => {
        setEditDataLoaded(true);
    }, [orderId]);

    useEffect(() => {
        if (data && editDataLoaded) {
            dispatch(setEditBookingDetails(data));
            setEditDataLoaded(false);
            if (Object.keys(data.guestDetails).length > 0) {
                setBookingFor('someone_else');
            } else {
                setBookingFor('customer');
            }
        }
    }, [data]);

    useEffect(() => {
        if (data && apartments && apartments.length > 0 && !originalApartment) {
            const selected = apartments.find(
                (apartment) =>
                    apartment.name === data.apartment.name,
            );
            if (selected) {
                dispatch(
                    setSelectedEditApartment(selected),
                );
                setOriginalApartment(selected.name);
            }
        }
    }, [data, apartments]);

    // Clear apartment details left over from a previously edited booking so we never
    // briefly show another asset's add-on services before this booking's details load.
    useEffect(() => {
        dispatch(setSelectedEditApartmentDetails(null));
    }, [dispatch]);

    useEffect(() => {
        const getApartmentDetails = async () => {
            dispatch(setSelectedEditApartmentDetails(null));
            if (selectedApartment) {
                await fetchApartmentDetails();
            }
        };
        getApartmentDetails();
        if (selectedApartment) {
            setLoadingApartments(false);
        }
    }, [selectedApartment]);

    const handleBookingFor = (selected: string) => {
        setBookingFor(selected);
        if (selected === 'customer') {
            dispatch(setForSomeoneElse({ isForSomeoneElse: false }));
        } else {
            dispatch(setForSomeoneElse({ isForSomeoneElse: true }));
        }
    };

    // Booking price single source of truth (#1203): the price box + save payload
    // read the backend breakdown from POST /admin/order/price-preview, keyed on
    // the edited dates / services / discount / already-paid. No FE total math.
    const previewArgs: PricePreviewArgs | undefined = useMemo(() => {
        const assetId = selectedApartment?.assetId;
        if (!assetId || !startDate || !endDate) return undefined;
        return {
            useCase: data?.useCase ?? 'apartment',
            assetId,
            // Calendar dates, not instants — `.toISOString()` shifts local
            // midnight to the previous UTC day and skews the night count when
            // one endpoint still carries a time component.
            checkIn: dayjs(startDate).format('YYYY-MM-DD'),
            checkOut: dayjs(endDate).format('YYYY-MM-DD'),
            services: additionalServices.map((s) => ({
                serviceId: s.id,
                quantity: s.quantity ?? 1,
            })),
            // #1734: price already-booked services from their snapshot rather
            // than today's catalog rate, so editing never re-prices the booking.
            ...(data?.id ? { orderId: data.id } : {}),
            // The class picked on this form: the backend re-quotes the stay
            // when it differs from the stored one, as the save reprices it.
            ...classFields,
            discount,
            amountPaid: alreadyPaid ?? 0,
        };
    }, [selectedApartment?.assetId, startDate, endDate, additionalServices, discount, alreadyPaid, data?.useCase, data?.id, classFields]);

    const { breakdown, previewStatus } = usePricePreview(previewArgs);
    // PO, 2026-10-30: the backend says whether this booking's customer mail
    // may go out; "save" stays available either way.
    const mailRefusal = customerMailRefusalOf(breakdown?.customerMail);
    const guardPriceOverride = usePriceOverrideGuard(setSnackbarData);

    const handleEditBooking = (saveMode: 'save_only' | 'save_and_send_email') => {
        const editBooking = async (saveMode: 'save_only' | 'save_and_send_email') => {
            if (saveMode === 'save_only') {
                setLoadingEditBookingOnlySave(true);
            } else {
                setLoadingEditBookingSaveAndSend(true);
            }
            const editRoomRentalBookingPayload: EditRoomRentalBookingPayload =
                {
                    // Untouched days send back the stored instants; a changed
                    // day is station midnight of the picked day (ADR-0019).
                    // #3219: the stored day is read on the booking's own clock,
                    // the new instant built on the target apartment's — a move
                    // to another zone on the same days sends its midnight.
                    checkIn: pickedOrStoredInstant(startDate, data?.checkIn, timeZone, bookingTimeZone),
                    checkOut: pickedOrStoredInstant(endDate, data?.checkOut, timeZone, bookingTimeZone),
                    city: city ?? '',
                    invoiceNumber: invoiceNumber ?? '',
                    numberOfGuests: numberOfGuests,
                    apartmentId: selectedApartment?.assetId ?? '',
                    locationId: selectedApartment?.locationId ?? '',
                    additionalServices: additionalServices.map((additionalService) => ({
                        serviceId: additionalService.id,
                        name: additionalService.serviceName,
                        quantity: additionalService.quantity ?? 1,
                    })),
                    customerDetails: {
                        firstName: personalDetails.firstName,
                        lastName: personalDetails.lastName,
                        email: personalDetails.email,
                        phone: personalDetails.phone,
                        address: personalDetails.address,
                        country: personalDetails.country,
                        city: personalDetails.city,
                        zipCode: personalDetails.zipCode,
                        company: personalDetails.company,
                    },
                    ...attributionPayloadForEdit(attributionSeededFor.current === orderId ? 'ok' : undefined, attribution),
                    // #1808: the picked class, once seeded; a change reprices on save.
                    ...classFields,
                    someoneElseDetails:
                        bookingFor === 'customer'
                            ? {}
                            : {
                                firstName: someoneElseDetails.firstName,
                                lastName: someoneElseDetails.lastName,
                                email: someoneElseDetails.email,
                                phone: someoneElseDetails.phone,
                            },
                    useCase: data?.useCase ?? 'apartment',
                    // Every money-bearing field below is omitted when the preview
                    // has not resolved. `adminEditBookingRoomRental` spreads each
                    // one conditionally, so an absent field leaves the stored
                    // value untouched — which is the only honest answer when we
                    // do not yet know the price. The previous `?? 'unpaid'` /
                    // `?? 0` defaults invented one instead: they downgraded an
                    // already-paid booking to unpaid, and re-billed it to 0,00 €
                    // via the manual override. Dates, contact details and comment
                    // still save normally, so this can never block an edit.
                    ...(breakdown
                        ? {
                            // BE-derived (#1203).
                            paymentStatus: breakdown.paymentStatus,
                            // #1000 Step 7C: the price adjustment rides the manual
                            // final-price override (SSoT re-bill), never the legacy
                            // discount voucher — so `discount` is always sent as 0
                            // (a surcharge would otherwise be a negative discount,
                            // which the API rejects). The signed adjustment lives
                            // in manualFinalPrice = BE total after the discount.
                            ...(discount !== 0
                                ? {
                                    // Guarded above, not clamped: a negative
                                    // total means the discount is stale, and
                                    // clamping to 0 re-bills the booking free.
                                    manualFinalPrice: Number(breakdown.total.toFixed(2)),
                                    priceAdjustmentReason: priceAdjustmentReason,
                                }
                                // No adjustment: clear a previously-stored override
                                // (so setting it back to 0 actually takes effect),
                                // else leave the booking un-overridden.
                                : data?.payment?.manualFinalPrice != null
                                    ? { clearPriceOverride: true }
                                    : {}),
                        }
                        : {}),
                    discount: 0,
                    alreadyPaid: alreadyPaid ?? 0,
                    comment: comment,
                    emailNote: emailNote,
                };

            const { success, reason, mailBlocked } = await editRoomRentalBooking(
                editRoomRentalBookingPayload,
                saveMode,
            );
            if (success) {
                setSnackbarData({
                    open: true,
                    // Saved — but a refused mail is said, never shown as a plain success.
                    ...savedOutcomeToast(t, mailBlocked, t('edit_booking.response.success')),
                });
                setTimeout(() => {
                    navigate(makeRentalBookingDetailsUrl(orderId));
                }, 1500);
                dispatch(clearBookingCreationStates());
                if (saveMode === 'save_only') {
                    setLoadingEditBookingOnlySave(false);
                } else {
                    setLoadingEditBookingSaveAndSend(false);
                }
            } else {
                setSnackbarData({
                    open: true,
                    message: t(editFailureMessageKey(reason)),
                    severity: 'error',
                });
                if (saveMode === 'save_only') {
                    setLoadingEditBookingOnlySave(false);
                } else {
                    setLoadingEditBookingSaveAndSend(false);
                    setOpen(false);
                }
            }
        };
        // Guarded out here rather than inside `editBooking`: the save flow is
        // already at the cognitive-complexity limit, and refusing before it
        // starts is where the check belongs anyway.
        if (!guardPriceOverride(breakdown)) return;
        editBooking(saveMode);
    };

    const handleCloseSnackbar = () => {
        setSnackbarData((prev) => ({ ...prev, open: false }));
    };

    useEffect(() => {
        if (!loadingCities && cities?.length === 1 && !city) {
            dispatch(setCity({ city: cities[0] }));
        }
    }, [cities, city, loadingCities, dispatch]);

    const handleRevertChanges = () => {
        if (data) {
            dispatch(setRevertEditBookingReservation(data));
            setPerformRevert(true);
        }
    };

    useEffect(() => {
        setAlreadyPaid(editBookingDetails.payment.amountPaid ?? 0);
        setDiscount(editBookingDetails.payment.couponApplied ?? 0);
    }, [editBookingDetails.payment.amountPaid, editBookingDetails.payment.couponApplied]);

    const apartmentHasError = originalApartment !== undefined
        && !apartmentNames.includes(originalApartment)
        && originalApartment === selectedApartment?.name;

    // The guest count may not exceed the object's capacity. The rule itself is
    // enforced by the API (ADR-0020); this explains it while the number is
    // being typed, and says what to do instead.
    //
    // Only a count the operator changed here blocks saving. A booking can
    // already carry a number above today's capacity — the capacity was lowered
    // afterwards, or it predates the guard — and holding those hostage would
    // block edits that never touch the guest count, which is exactly what
    // locked N-00000840 out of an internal note.
    const {
        capacity: objectCapacity,
        exceeds: guestsExceedCapacity,
        blocksSave: guestsBlockSave,
    } = useGuestCapacityGuard({
        selectedObject: selectedApartment,
        selectedObjectDetails: selectedApartmentDetails,
        guests: numberOfGuests,
        savedGuests: data?.numberOfGuests,
    });

    const saveButtonDisabled = loadingEditBookingSaveAndSend
        || loadingEditBookingOnlySave
        || !confirmEnabled
        || apartmentHasError
        || guestsBlockSave;

    const isCanceled = editBookingDetails?.status === 'canceled';
    // eslint-disable-next-line no-console
    console.log('saveButtonDisabled', saveButtonDisabled);
    // eslint-disable-next-line no-console
    console.log('loadingEditBookingSaveAndSend', loadingEditBookingSaveAndSend);
    // eslint-disable-next-line no-console 
    console.log('loadingEditBookingOnlySave', loadingEditBookingOnlySave);
    // eslint-disable-next-line no-console
    console.log('confirmEnabled', confirmEnabled);
    // eslint-disable-next-line no-console
    console.log('apartmentHasError', apartmentHasError);

    return (
        <Container maxWidth={false} className={'px-0 sm:px-4'}>
            {isFetching ? (
                <Box sx={{ py: 3 }}>
                    <div
                        className={
                            'flex w-full h-[80vh] items-center justify-center'
                        }
                    >
                        <CircularProgress />
                    </div>
                </Box>
            ) : (
                <Box sx={{ py: 3, pt: 3 }}>
                    <PageHeader>
                        <PageHeadline>
                            {t('booking_details.title')}
                        </PageHeadline>
                    </PageHeader>    
                    <div className={'mb-6'}>
                        <CustomSeparator items={breadcrumbItems} />
                    </div>
                    <Paper sx={styles.paper}>
                        {/* Header summary */}
                        <div
                            className={`
                        flex items-start justify-between mb-4 bg-[#F5F5F5]
                        p-3
                        `}
                        >
                            <div className={'flex flex-1 flex-col'}>
                                <div className={'flex items-center mb-2'}>
                                    <div className={'flex flex-col lg:flex-row'}>
                                        <h1
                                            className={
                                                'text-2xl font-medium mr-4'
                                            }
                                        >
                                            {data?.bookingNumber}
                                        </h1>
                                        <div className={'flex flex-row mt-2 xl:mt-0'}>
                                            <StatusBadge
                                                type={
                                                    editBookingDetails?.status ??
                                                    'booked'
                                                }
                                                isEditing={true}
                                                options={[
                                                    {
                                                        label: t(
                                                            'booking_details.status.active',
                                                        ),
                                                        value: 'active',
                                                    },
                                                    {
                                                        label: t(
                                                            'booking_details.status.booked',
                                                        ),
                                                        value: 'booked',
                                                    },
                                                    {
                                                        label: t(
                                                            'booking_details.status.canceled',
                                                        ),
                                                        value: 'canceled',
                                                    },
                                                    {
                                                        label: t(
                                                            'booking_details.status.finished',
                                                        ),
                                                        value: 'finished',
                                                    },
                                                ]}
                                            />
                                            <div className={'mx-2'}></div>
                                            <StatusBadge
                                                // Covered reads "Abgedeckt", as on the details page.
                                                type={editPaymentBadgeStatus(data, editBookingDetails?.payment_status)}
                                                showDropdown={false}
                                                isEditing={true}
                                                options={[
                                                    {
                                                        label: t(
                                                            'booking_details.status.paid',
                                                        ),
                                                        value: 'paid',
                                                    },
                                                    {
                                                        label: t(
                                                            'booking_details.status.unpaid',
                                                        ),
                                                        value: 'unpaid',
                                                    },
                                                ]}
                                                onSelect={(val) => Logger(val)}
                                            />
                                            <div className={'mx-2'}></div>
                                            <StatusBadge
                                                type={editBookingDetails?.isMailSent === true ? 'sent' : 'not_sent'}
                                                showDropdown={false}
                                                isEditing={false}
                                                options={[
                                                    {
                                                        label: t('booking_details.mail_sent.mail_sent'),
                                                        value: 'sent',
                                                    },
                                                    {
                                                        label: t('booking_details.mail_sent.mail_not_sent'),
                                                        value: 'not_sent',
                                                    },
                                                ]}
                                            />
                                        </div>

                                    </div>
                                </div>
                                <div className={'flex w-full gap-4 flex-col items-start sm:flex-row sm:justify-center sm:items-center'}>
                                    <div className={'flex-1'}>
                                        <p className={'text-sm text-gray-500 mb-1'}>
                                            {t('booking_details.created_on')}:{' '}
                                            <span className={'font-bold'}>
                                                {formatDateForDisplay(data?.created_at!, bookingTimeZone)}
                                            </span>
                                        </p>
                                        <p className={'text-sm text-gray-500'}>
                                            {t('booking_details.last_update')}:{' '}
                                            <span className={'font-bold'}>
                                                {formatDateForDisplay(data?.updated_at!, bookingTimeZone)}
                                            </span>
                                        </p>
                                        {data?.setupTime && <p className={'text-sm text-gray-500 mt-2'}>
                                            <span className={'inline-flex items-center gap-1'}>
                                                {t('booking_details.buffer_from')}:
                                                <InfoTooltip text={t('booking_details.buffer_from_tooltip')} />
                                            </span>{' '}
                                            <span
                                                className={'font-bold'}
                                            >
                                                {formatDateTime(data.setupTime, i18n.language as 'en' | 'de', { includeTime: true, timeZone: bookingTimeZone })}
                                            </span>
                                        </p>}
                                    </div>
                                    {/* #1637: retired for Mode A, with or without a number circle (no
                                        scheme, no invoicing: ADR-0012, PO 2026-09-29). The statutory number is allocated by
                                        kolula and lives on the invoice record; a free-text field beside it
                                        would let an operator type a competing one. Mode C tenants keep it —
                                        their ERP's number belongs here. */}
                                    {!isInvoiceIssuingTenant && (
                                        <div className={'flex-1 w-full'}>
                                            <Typography variant={'body2'} className={'mb-2 font-bold text-foreground'}>
                                                {t('create_booking.reservation_details.invoice_nr')}
                                            </Typography>
                                            <InputField
                                                onChange={(value) =>
                                                    dispatch(
                                                        setEditInvoiceNumber({
                                                            invoiceNumber: value,
                                                        }),
                                                    )
                                                }
                                                disabled={isCanceled}
                                                value={invoiceNumber}
                                                label={t(
                                                    'create_booking.reservation_details.invoice_nr',
                                                )}
                                            />
                                        </div>
                                    )}
                                </div>
                            </div>
                           
                        </div>
                        <Typography variant={'h3'} className={'mb-3 mt-5'}>
                            {t('create_booking.reservation_details.title')}
                        </Typography>
                        <Grid
                            container
                            sx={{
                                width: '100%',
                                justifyContent: 'flex-start',
                                alignItems: 'flex-start',
                                display: 'flex',
                            }}
                        >
                            <Grid
                                container
                                spacing={2}
                                className={'w-[100%] xl:w-[30%] mt-1 xl:mt-0 mb-8 xl:mb-0 flex justify-end items-end order-1 xl:order-2'}
                            >
                                <ReservationSummary
                                    isCreateBooking={false}
                                    isEditBooking={true}
                                    breakdown={breakdown}
                                    previewStatus={previewStatus}
                                    couponApplied={discount}
                                    amountPaid={alreadyPaid}
                                    onAlreadyPaidChange={(newAlreadyPaid) => setAlreadyPaid(newAlreadyPaid)}
                                    onDiscountChange={(newDiscount) => setDiscount(newDiscount)}
                                    coveredBy={coveredByOf(data)}
                                />
                                <PriceAdjustmentReasonField
                                    discount={discount}
                                    value={priceAdjustmentReason}
                                    onChange={setPriceAdjustmentReason}
                                    disabled={isCanceled}
                                />
                            </Grid>
                            <Grid
                                container
                                spacing={2}
                                className={'w-[100%] xl:w-[70%] flex justify-start items-start order-2 xl:order-1'}
                            >
                                <Grid item xs={12} sm={6}>
                                    <DateRangeField
                                        from={startDate ? dayjs(startDate) : null}
                                        to={endDate ? dayjs(endDate) : null}
                                        onChange={(from, to) => handleDateChange(from, to)}
                                        disabled={isCanceled}
                                        disablePast
                                        allowedStartDays={availableCheckInDates}
                                        allowedEndDays={availableCheckOutDates}
                                        maxDate={dayjs().add(18, 'month').endOf('month')}
                                        label={t('create_booking.reservation_details.booking_period') + ' *'}
                                    />
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <SelectField
                                        options={cities ?? ['']}
                                        value={city ?? ''}
                                        onChange={(value) =>
                                            dispatch(setCity({ city: value }))
                                        }
                                        disabled={cities.length === 1 || isCanceled}
                                        loading={loadingCities}
                                        label={
                                            t(
                                                'create_booking.reservation_details.city',
                                            ) + ' *'
                                        }
                                    />
                                </Grid>

                                <Grid item xs={12} sm={6}>
                                    <InputField
                                        type={'number'}
                                        min={1}
                                        integerOnly
                                        onChange={(value) =>
                                            dispatch(
                                                setEditNumberOfGuests({
                                                    numberOfGuests: Number(value),
                                                }),
                                            )
                                        }
                                        disabled={isCanceled}
                                        value={String(numberOfGuests ?? '')}
                                        error={guestsExceedCapacity}
                                        errorText={t(
                                            'create_booking.reservation_details.guests_exceed_object_capacity',
                                            { capacity: objectCapacity ?? 0 },
                                        )}
                                        label={
                                            t(
                                                'create_booking.reservation_details.number_of_guests',
                                            ) + ' *'
                                        }
                                    />
                                </Grid>

                                {/* #1808: the same select as create, pre-filled with the
                                    stored override; absent unless the tenant has more than
                                    the seeded default class, or before the booking's class
                                    was read. */}
                                {classSeeded && (
                                    <Grid item xs={12} sm={6}>
                                        <BookingCustomerClassSelect
                                            value={customerClassId}
                                            inheritedClassId={noOverrideClassId}
                                            onChange={setCustomerClassId}
                                            disabled={isCanceled}
                                        />
                                    </Grid>
                                )}

                                <Grid item xs={12} sm={12}>
                                    <SelectField
                                        options={apartmentNames ?? []}
                                        value={selectedApartment?.name ?? ''}
                                        onChange={(apart) => {
                                            if (apartments) {
                                                const selected =
                                                    apartments.find(
                                                        (apartment) =>
                                                            apartment.name ===
                                                            apart,
                                                    );
                                                if (selected) {
                                                    dispatch(
                                                        setSelectedEditApartment(
                                                            selected,
                                                        ),
                                                    );
                                                    markPendingRemap();
                                                }
                                            }
                                        }}
                                        disabled={isCanceled}
                                        error={apartmentHasError}
                                        label={
                                            t(
                                                'create_booking.reservation_details.apartment',
                                            ) + ' *'
                                        }
                                        // The create screen already names the
                                        // reason; without this the edit screen
                                        // falls back to the select's default
                                        // "No options available", which reads
                                        // as "nothing matches" and never as
                                        // "here is why" — the state Neuland
                                        // reported on N-00000840.
                                        noOptions={t(
                                            'create_booking.reservation_details.no_apartments_match',
                                        )}
                                    />
                                    {
                                        selectedApartment &&
                                        <Typography variant={'body1'} fontWeight={'bold'} sx={{ mt: 2, color: 'gray' }}>
                                            {/* eslint-disable-next-line max-len */}
                                            {`${extractAddress(selectedApartment?.name)}, ${selectedApartment?.street}, ${selectedApartment?.postalCode} ${selectedApartment?.city}`}
                                        </Typography>
                                    }
                                    {
                                        (originalApartment && selectedApartment?.name === originalApartment && !apartmentNames.includes(originalApartment)) &&
                                        <Alert
                                            severity={'warning'}
                                            sx={{
                                                mt: 2,
                                                backgroundColor: '#FCEEC680',
                                                color: '#333333',
                                                fontSize: '14px',
                                                '& .MuiAlert-icon': {
                                                    color: '#F3BB1B',
                                                },
                                                '& a': {
                                                    color: '#333333',
                                                    textDecoration: 'underline',
                                                    cursor: 'pointer',
                                                },
                                            }}
                                        >
                                            <Trans
                                                i18nKey={'create_booking.reservation_details.original_apartment_not_available'}
                                                values={{ apartment: originalApartment }}
                                                components={[
                                                    <button
                                                        type={'button'}
                                                        onClick={handleRevertChanges}
                                                        style={{
                                                            all: 'unset',
                                                            color: 'inherit',
                                                            cursor: 'pointer',
                                                            textDecoration: 'underline',
                                                        }}
                                                    />,
                                                ]}
                                            />
                                        </Alert>
                                    }
                                </Grid>
                            </Grid>
                        </Grid>

                        <Divider
                            sx={{ my: 3, backgroundColor: 'light-gray' }}
                        />
                        <Typography variant={'h3'} className={'mb-2'}>
                            {t('create_booking.additional_services.title')}
                        </Typography>
                        {selectedApartment ? (
                            selectedApartmentDetails ? (
                                isCanceled ?
                                    <BookingDetailsAdditionalServices
                                        additionalServices={additionalServices}
                                        allAdditionalServices={
                                            allAdditionalServices ?? editBookingDetails?.allAdditionalServices!
                                        }
                                    />                               
                                    :
                                    <BookingEditDetailsAdditionalServices
                                        additionalServices={additionalServices}
                                        allAdditionalServices={
                                            allAdditionalServices ?? editBookingDetails?.allAdditionalServices!
                                        }
                                        bookingCreatedAt={bookedAssetStillSelected ? data?.created_at : undefined}
                                        bookedCompulsoryServices={data?.payment?.compulsory}
                                        unavailableServices={servicesUnavailableAtVenue}
                                    />
                            ) : (
                                <div
                                    className={
                                        'flex items-center space-x-2 text-gray-500'
                                    }
                                >
                                    <CircularProgress
                                        className={'text-gray-500'}
                                        size={20}
                                    />
                                    <Typography className={'text-gray-500'}>
                                        {t('common.loading')}
                                    </Typography>
                                </div>
                            )
                        ) : (
                            <Typography>
                                {t('create_booking.additional_services.note')}
                            </Typography>
                        )}
                        <Divider
                            sx={{ my: 3, backgroundColor: 'light-gray' }}
                        />
                        <Typography variant={'h3'} className={'mb-5'}>
                            {t('create_booking.customer_details.title')}
                        </Typography>
                        <Grid
                            container={true}
                            spacing={2}
                            className={'w-[100%] xl:w-[70%]'}
                            sx={{ justifyContent: 'flex-start' }}
                        >
                            {/* First row with two Selects */}
                            <Grid item xs={12} sm={6}>
                                <InputField
                                    onChange={onFirstNameChange}
                                    disabled={isCanceled}
                                    value={personalDetails?.firstName ?? ''}
                                    label={
                                        t(
                                            'create_booking.customer_details.first_name',
                                        ) + ' *'
                                    }
                                />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <InputField
                                    onChange={onLastNameChange}
                                    disabled={isCanceled}
                                    value={personalDetails?.lastName ?? ''}
                                    label={
                                        t(
                                            'create_booking.customer_details.last_name',
                                        ) + ' *'
                                    }
                                />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <InputField
                                    onChange={onEmailChange}
                                    disabled={isCanceled}
                                    value={personalDetails?.email ?? ''}
                                    label={
                                        t(
                                            'create_booking.customer_details.email',
                                        )
                                    }
                                />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <PhoneNumberComponent
                                    enabled={!isCanceled}
                                    initialValue={personalDetails?.phone ?? ''}
                                    onChange={onPhoneChange}
                                    valid={personalDataValidation.phone}
                                    required={false}
                                    label={
                                        t(
                                            'create_booking.customer_details.phone',
                                        )
                                    }
                                />
                            </Grid>
                            <Grid item xs={12} sm={12}>
                                <InputField
                                    onChange={onCompanyNameChange}
                                    disabled={isCanceled}
                                    value={personalDetails.company}
                                    label={
                                        t(
                                            'create_booking.customer_details.company',
                                        )
                                    }
                                />
                            </Grid>
                            <Grid item xs={12} sm={12}>
                                <BookingAttributionFields
                                    value={attribution}
                                    onChange={setAttribution}
                                    onOrganizationPicked={(prefill) => applyContactPrefill(prefill, {
                                        company: onCompanyNameChange,
                                        address: onAddressChange,
                                        zipCode: onZipCodeChange,
                                        city: onCityChange,
                                        country: onCountryChange,
                                    })}
                                    disabled={isCanceled}
                                    organizationLocked={data?.coveredByOrg === true}
                                    loadFailed={data?.attributionStatus === 'failed'}
                                />
                            </Grid>
                            <Grid item xs={12} sm={12}>
                                <InputField
                                    onChange={onAddressChange}
                                    disabled={isCanceled}
                                    value={personalDetails?.address ?? ''}
                                    label={
                                        t(
                                            'create_booking.customer_details.address',
                                        )
                                    }
                                />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <SelectField
                                    options={countryOptions}
                                    value={toCountryLabel(personalDetails?.country ?? '')}
                                    onChange={(label) => onCountryChange(toCountryValue(label))}
                                    disabled={isCanceled}
                                    label={
                                        t(
                                            'create_booking.customer_details.country',
                                        )
                                    }
                                />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <InputField
                                    onChange={onCityChange}
                                    disabled={isCanceled}
                                    value={personalDetails?.city ?? ''}
                                    label={
                                        t(
                                            'create_booking.customer_details.city',
                                        )
                                    }
                                />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <InputField
                                    onChange={onZipCodeChange}
                                    disabled={isCanceled}
                                    value={personalDetails?.zipCode ?? ''}
                                    label={
                                        t(
                                            'create_booking.customer_details.zip_code',
                                        )
                                    }
                                />
                            </Grid>
                            <Grid item xs={12} sm={12}>
                                <InputField
                                    onChange={onCommentChange}
                                    disabled={isCanceled}
                                    value={comment}
                                    label={t(commentLabelKey(createdBy))}
                                />
                            </Grid>
                            <Grid item xs={12} sm={12}>
                                <InputField
                                    onChange={onEmailNoteChange}
                                    disabled={isCanceled}
                                    value={emailNote ?? ''}
                                    maxLength={BOOKING_EMAIL_NOTE_MAX_LENGTH}
                                    label={
                                        t(
                                            'create_booking.customer_details.email_note',
                                        )
                                    }
                                />
                            </Grid>
                        </Grid>

                        <Divider sx={{ my: 3, backgroundColor: 'light-gray' }} />
                        <Typography variant={'h3'} className={'mb-3'}>
                            {t('create_booking.booking_for.title')}
                        </Typography>
                        <FormControl component={'fieldset'} disabled={isCanceled}>
                            <RadioGroup
                                row
                                value={bookingFor}
                                onChange={(e) => handleBookingFor(e.target.value)}
                                sx={{ paddingLeft: '13px', marginBottom: '14px' }}
                            >
                                <FormControlLabel
                                    value={'customer'}
                                    control={
                                        <Radio
                                            sx={{
                                                '&.Mui-checked': {
                                                    color: theme.palette.primary.light, // Cyan/turquoise color
                                                },
                                            }}
                                        />
                                    }
                                    label={t('create_booking.booking_for.customer')}
                                />
                                <FormControlLabel
                                    value={'someone_else'}
                                    control={
                                        <Radio
                                            sx={{
                                                '&.Mui-checked': {
                                                    color: theme.palette.primary.light, // Cyan/turquoise color
                                                },
                                            }}
                                        />
                                    }
                                    label={t('create_booking.booking_for.someone_else')}
                                />
                            </RadioGroup>
                        </FormControl>

                        {bookingFor === 'someone_else' && (
                            <Divider
                                sx={{ my: 3, backgroundColor: 'light-gray' }}
                            />
                        )}
                        {bookingFor === 'someone_else' && (
                            <Typography variant={'h3'} className={'mb-3'}>
                                {t('booking_details.guest')}
                            </Typography>
                        )}

                        {bookingFor === 'someone_else' && (
                            <Grid
                                container={true}
                                spacing={2}
                                className={'w-[100%] xl:w-[70%]'}
                                sx={{
                                    justifyContent: 'flex-start',
                                }}
                            >
                                <Grid item xs={12} sm={6}>
                                    <InputField
                                        onChange={onSomeoneElseFirstNameChange}
                                        disabled={isCanceled}
                                        value={someoneElseDetails?.firstName}
                                        label={
                                            t(
                                                'create_booking.booking_for.first_name',
                                            ) + ' *'
                                        }
                                    />
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <InputField
                                        onChange={onSomeoneElseLastNameChange}
                                        disabled={isCanceled}
                                        value={someoneElseDetails?.lastName}
                                        label={
                                            t(
                                                'create_booking.booking_for.last_name',
                                            ) + ' *'
                                        }
                                    />
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <InputField
                                        onChange={onSomeoneElseEmailChange}
                                        disabled={isCanceled}
                                        value={someoneElseDetails?.email}
                                        label={
                                            t(
                                                'create_booking.booking_for.email',
                                            ) + ' *'
                                        }
                                    />
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <PhoneNumberComponent
                                        enabled={!isCanceled}
                                        initialValue={someoneElseDetails?.phone}
                                        onChange={onSomeoneElsePhoneChange}
                                        valid={
                                            someoneElseDetails.phone.length > 1
                                        }
                                        required={true}
                                        label={
                                            t(
                                                'create_booking.booking_for.phone',
                                            )
                                        }
                                    />
                                </Grid>
                            </Grid>
                        )}
                        <div className={'flex space-x-2 mt-6'}>
                            <CustomButton
                                title={t(
                                    'edit_booking.button.cancel',
                                )}
                                onClick={() => navigate(-1)}
                                isFilled={false}
                            />
                            <CustomButton
                                title={t(
                                    'edit_booking.button.confirm_only',
                                )}
                                onClick={() => handleEditBooking('save_only')}
                                loading={loadingEditBookingOnlySave}
                                disabled={saveButtonDisabled}
                                isFilled={false}
                            />
                            <CustomButton
                                title={t(
                                    sendCopy.button,
                                )}
                                onClick={() => setOpen(true)}
                                isFilled={true}
                                // loading={loadingEditBookingSaveAndSend}
                                disabled={saveButtonDisabled || !personalDataValidation.email || !!mailRefusal}
                            />
                        </div>
                        <CustomerMailBlockedNote refusal={mailRefusal} />
                        <SnackbarComponent
                            open={snackbarData.open}
                            message={snackbarData.message}
                            severity={snackbarData.severity}
                            onClose={handleCloseSnackbar}
                        />
                        <CustomModal
                            open={open}
                            onClose={() => setOpen(false)}
                            title={t(sendCopy.title)}
                            saveButton={
                                <CustomButton
                                    title={t('edit_booking.modal.confirm_button')}
                                    onClick={() => handleEditBooking('save_and_send_email')}
                                    disabled={saveButtonDisabled || !personalDataValidation.email || !!mailRefusal}
                                    loading={loadingEditBookingSaveAndSend}
                                    isFilled={true}
                                    fullWidth={false}
                                />
                            }
                            closeButton={
                                <CustomButton
                                    title={t(
                                        'edit_booking.modal.cancel_button',
                                    )}
                                    onClick={() => setOpen(false)}
                                    isFilled={false}
                                />
                            }
                            description={t(sendCopy.description)}
                        >
                        </CustomModal>
                    </Paper>
                </Box>
            )}
        </Container>
    );
};

export default EditRoomBooking;
